// Helpers for opening-hours: today-line matching for display, and
// open-now status from structured periods + the POI's UTC offset.

import type { OpeningPeriod } from '~/shared/api.js';

const ENGLISH_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Find the entry in `openingHours` whose day name matches today.
 * Returns null when no entry matches — the caller then falls back to
 * showing the full schedule (current pre-collapse behavior).
 *
 * `locale` should be the same code the hours were fetched in (i.e.,
 * the trip's resolved `languageCode`). When provided, the matcher
 * formats today's weekday in that locale (`月曜日` for `ja`, `lundi`
 * for `fr`, etc.) so it lines up with what Google returned.
 *
 * Match strategy:
 *   1. Try the explicit `locale` (the trip's language).
 *   2. Try the browser's default locale.
 *   3. Fall back to English day names.
 *
 * Today is determined by the user's browser timezone, not the POI's.
 * Wrong-day risk near midnight is small and acceptable for v1.
 */
export function findTodayHoursLine(
  openingHours: string[],
  locale?: string | null,
): string | null {
  if (openingHours.length === 0) return null;
  const now = new Date();
  const tryLocale = (loc: string | undefined): string | null => {
    try {
      const day = new Intl.DateTimeFormat(loc, { weekday: 'long' }).format(now);
      return openingHours.find((line) => line.startsWith(day)) ?? null;
    } catch {
      return null;
    }
  };
  if (locale) {
    const hit = tryLocale(locale);
    if (hit) return hit;
  }
  const browserHit = tryLocale(undefined);
  if (browserHit) return browserHit;
  const english = ENGLISH_DAYS[now.getDay()];
  if (!english) return null;
  return openingHours.find((line) => line.startsWith(english)) ?? null;
}

/**
 * Is the POI currently open?
 *
 * Pure function. Returns `true` / `false` based on the periods, or
 * `null` when we don't have enough data to decide (legacy POIs from
 * before migration 014, or places Google returns no periods for).
 *
 * `utcOffsetMinutes` is the POI's offset, not the viewer's. So a
 * Tokyo place at 14:00 JST evaluates against 14:00 JST regardless of
 * where the viewer's browser thinks it is. We project `now` into the
 * POI's local clock by adding the POI's offset to the UTC timestamp.
 *
 * Period model: Google's day field is 0=Sun ... 6=Sat in the POI's
 * local time. A period with `close.day > open.day` (or wrap to next
 * week) crosses midnight; we evaluate by converting both endpoints
 * to "minutes since Sunday 00:00" and checking that the current
 * minutes fall in [open, close). A period with no `close` means
 * "open 24h on that day" (per Places API spec) — we treat that as
 * "always open" for the matched day.
 */
export function isOpenNow(
  periods: OpeningPeriod[] | null,
  utcOffsetMinutes: number | null,
  now: Date = new Date(),
): boolean | null {
  if (!periods || periods.length === 0) return null;
  if (utcOffsetMinutes === null) return null;

  // Project `now` into the POI's local clock. getTime() is UTC ms;
  // add the POI's offset (in ms) and read the resulting weekday/
  // hour/minute as if it were UTC — that's the POI's local time.
  const projected = new Date(now.getTime() + utcOffsetMinutes * 60_000);
  const nowMinutes =
    projected.getUTCDay() * 24 * 60 +
    projected.getUTCHours() * 60 +
    projected.getUTCMinutes();

  for (const p of periods) {
    const openMin = p.open.day * 24 * 60 + p.open.hour * 60 + p.open.minute;
    if (!p.close) {
      // 24h on this day: open all day on that weekday.
      if (projected.getUTCDay() === p.open.day) return true;
      continue;
    }
    let closeMin = p.close.day * 24 * 60 + p.close.hour * 60 + p.close.minute;
    // Wrap to next week if close is earlier in the week than open
    // (Saturday 10pm → Sunday 2am).
    if (closeMin <= openMin) closeMin += 7 * 24 * 60;
    // Same-week match.
    if (nowMinutes >= openMin && nowMinutes < closeMin) return true;
    // Wrap-week match: a period that started last Saturday at 22:00
    // might still be open this Sunday at 01:30 (nowMinutes < openMin
    // but nowMinutes + 7 days falls inside [openMin, closeMin]).
    if (nowMinutes + 7 * 24 * 60 >= openMin && nowMinutes + 7 * 24 * 60 < closeMin) {
      return true;
    }
  }
  return false;
}
